import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

const STATUS_COLORS = {
  pending: { bg: 'rgba(251,191,36,0.12)', text: '#fbbf24', border: 'rgba(251,191,36,0.3)', label: 'Pending' },
  reviewing: { bg: 'rgba(96,165,250,0.12)', text: '#60a5fa', border: 'rgba(96,165,250,0.3)', label: 'Reviewing' },
  approved: { bg: 'rgba(52,211,153,0.12)', text: '#34d399', border: 'rgba(52,211,153,0.3)', label: 'Approved' },
  rejected: { bg: 'rgba(239,68,68,0.12)', text: '#ef4444', border: 'rgba(239,68,68,0.3)', label: 'Rejected' },
};

const CorporateLeaseRequestsQueue = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [expandedId, setExpandedId] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [toast, setToast] = useState(null);
  const [convertModal, setConvertModal] = useState(null); // { request }
  const [convertLoading, setConvertLoading] = useState(false);
  const [adminNotes, setAdminNotes] = useState({});

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        ?.from('corporate_lease_requests')
        ?.select('*, user_profiles!corporate_lease_requests_user_id_fkey(full_name, email, phone)')
        ?.order('created_at', { ascending: false });

      if (filterStatus !== 'all') {
        query = query?.eq('intake_status', filterStatus);
      }

      const { data, error: fetchErr } = await query;
      if (fetchErr) throw fetchErr;
      setRequests(data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load lease requests');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const updateStatus = async (requestId, newStatus) => {
    setActionLoading(requestId + newStatus);
    try {
      const notes = adminNotes?.[requestId] || null;
      const { error: updateErr } = await supabase
        ?.from('corporate_lease_requests')
        ?.update({
          intake_status: newStatus,
          req_status: newStatus === 'approved' ? 'approved' : newStatus === 'rejected' ? 'rejected' : 'submitted',
          admin_notes: notes,
          updated_at: new Date()?.toISOString(),
        })
        ?.eq('id', requestId);

      if (updateErr) throw updateErr;
      showToast(`Request ${newStatus} successfully`);
      fetchRequests();
    } catch (err) {
      showToast(err?.message || 'Action failed', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleConvertToAccount = async () => {
    if (!convertModal?.request) return;
    const req = convertModal?.request;
    setConvertLoading(true);
    try {
      // 1. Create corporate_accounts row (admin-only)
      const { data: account, error: accountErr } = await supabase
        ?.from('corporate_accounts')
        ?.insert({
          company_name: req?.company_name || 'Unknown Company',
          billing_email: req?.email || '',
          contact_person: req?.contact_name || null,
          contact_phone: req?.phone || null,
          corp_status: 'active',
          source_request_id: req?.id,
          approved_at: new Date()?.toISOString(),
        })
        ?.select()
        ?.single();

      if (accountErr) throw accountErr;

      // 2. Mark the request as approved and link to new account
      const { error: updateErr } = await supabase
        ?.from('corporate_lease_requests')
        ?.update({
          intake_status: 'approved',
          req_status: 'approved',
          corporate_id: account?.id,
          updated_at: new Date()?.toISOString(),
        })
        ?.eq('id', req?.id);

      if (updateErr) throw updateErr;

      showToast(`Corporate account created for ${req?.company_name}`);
      setConvertModal(null);
      fetchRequests();
    } catch (err) {
      showToast(err?.message || 'Failed to create corporate account', 'error');
    } finally {
      setConvertLoading(false);
    }
  };

  const formatDate = (ts) => {
    if (!ts) return '—';
    return new Date(ts)?.toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const statusCounts = requests?.reduce((acc, r) => {
    acc[r?.intake_status] = (acc?.[r?.intake_status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="bg-surface rounded-xl border border-border p-5 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-text-primary font-bold text-lg flex items-center gap-2">
            <Icon name="FileText" size={20} className="text-[#ffd700]" />
            Corporate Lease Requests
          </h2>
          <p className="text-text-secondary text-sm mt-0.5">Review and manage inbound lease enquiries</p>
        </div>
        <button
          onClick={fetchRequests}
          className="p-2 rounded-lg border border-border hover:bg-surface-hover transition-colors"
          title="Refresh"
        >
          <Icon name="RefreshCw" size={15} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Status Filter Tabs */}
      <div className="flex flex-wrap gap-2">
        {['all', 'pending', 'reviewing', 'approved', 'rejected']?.map(s => (
          <button
            key={s}
            onClick={() => setFilterStatus(s)}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all capitalize"
            style={{
              background: filterStatus === s ? 'rgba(255,215,0,0.15)' : 'rgba(255,255,255,0.05)',
              color: filterStatus === s ? '#ffd700' : 'var(--color-text-secondary)',
              border: `1px solid ${filterStatus === s ? 'rgba(255,215,0,0.3)' : 'rgba(255,255,255,0.08)'}`,
            }}
          >
            {s === 'all' ? 'All' : STATUS_COLORS?.[s]?.label}
            {s !== 'all' && statusCounts?.[s] ? (
              <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-[10px]" style={{ background: 'rgba(255,255,255,0.1)' }}>
                {statusCounts?.[s]}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 rounded-lg text-sm" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444' }}>
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="space-y-3">
          {[...Array(3)]?.map((_, i) => (
            <div key={i} className="h-20 rounded-xl bg-border animate-pulse" style={{ animationDelay: `${i * 80}ms` }} />
          ))}
        </div>
      )}

      {/* Empty */}
      {!loading && !error && requests?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Inbox" size={40} className="text-text-secondary mx-auto mb-3 opacity-40" />
          <p className="text-text-secondary text-sm">No {filterStatus !== 'all' ? filterStatus : ''} lease requests found</p>
        </div>
      )}

      {/* Request Cards */}
      {!loading && requests?.map(req => {
        const statusStyle = STATUS_COLORS?.[req?.intake_status] || STATUS_COLORS?.pending;
        const isExpanded = expandedId === req?.id;
        const reqOptions = Array.isArray(req?.requested_options) ? req?.requested_options : [];
        const reqDetails = req?.requirements || {};

        return (
          <div
            key={req?.id}
            className="rounded-xl border transition-all"
            style={{ background: 'rgba(255,255,255,0.03)', borderColor: isExpanded ? 'rgba(255,215,0,0.2)' : 'rgba(255,255,255,0.08)' }}
          >
            {/* Card Header */}
            <div
              className="p-4 cursor-pointer flex items-start justify-between gap-3"
              onClick={() => setExpandedId(isExpanded ? null : req?.id)}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-text-primary font-semibold text-sm truncate">
                    {req?.company_name || 'Unknown Company'}
                  </span>
                  <span
                    className="px-2 py-0.5 rounded-full text-xs font-semibold"
                    style={{ background: statusStyle?.bg, color: statusStyle?.text, border: `1px solid ${statusStyle?.border}` }}
                  >
                    {statusStyle?.label}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-1 flex-wrap">
                  <span className="text-text-secondary text-xs flex items-center gap-1">
                    <Icon name="User" size={11} /> {req?.contact_name || '—'}
                  </span>
                  <span className="text-text-secondary text-xs flex items-center gap-1">
                    <Icon name="Mail" size={11} /> {req?.email || '—'}
                  </span>
                  <span className="text-text-secondary text-xs flex items-center gap-1">
                    <Icon name="Car" size={11} /> {req?.fleet_size || 1} vehicle{req?.fleet_size !== 1 ? 's' : ''}
                  </span>
                  <span className="text-text-secondary text-xs flex items-center gap-1">
                    <Icon name="Calendar" size={11} /> {req?.lease_term || 12} months
                  </span>
                  <span className="text-text-secondary text-xs">{formatDate(req?.created_at)}</span>
                </div>
              </div>
              <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-text-secondary flex-shrink-0 mt-1" />
            </div>

            {/* Expanded Details */}
            {isExpanded && (
              <div className="px-4 pb-4 space-y-4 border-t border-white/5 pt-4">
                {/* Details Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {req?.phone && (
                    <div className="rounded-lg p-3" style={{ background: 'rgba(255,255,255,0.04)' }}>
                      <p className="text-text-secondary text-xs mb-0.5">Phone</p>
                      <p className="text-text-primary text-sm font-medium">{req?.phone}</p>
                    </div>
                  )}
                  {reqDetails?.vehicleTypes?.length > 0 && (
                    <div className="rounded-lg p-3" style={{ background: 'rgba(255,255,255,0.04)' }}>
                      <p className="text-text-secondary text-xs mb-1">Vehicle Types</p>
                      <div className="flex flex-wrap gap-1">
                        {reqDetails?.vehicleTypes?.map(t => (
                          <span key={t} className="px-2 py-0.5 rounded-full text-xs" style={{ background: 'rgba(255,215,0,0.1)', color: '#ffd700' }}>{t}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {reqDetails?.budgetRange && (
                    <div className="rounded-lg p-3" style={{ background: 'rgba(255,255,255,0.04)' }}>
                      <p className="text-text-secondary text-xs mb-0.5">Budget Range</p>
                      <p className="text-text-primary text-sm font-medium">KES {reqDetails?.budgetRange}</p>
                    </div>
                  )}
                  {reqDetails?.startDate && (
                    <div className="rounded-lg p-3" style={{ background: 'rgba(255,255,255,0.04)' }}>
                      <p className="text-text-secondary text-xs mb-0.5">Preferred Start</p>
                      <p className="text-text-primary text-sm font-medium">{formatDate(reqDetails?.startDate)}</p>
                    </div>
                  )}
                  {reqDetails?.estimatedMileage && (
                    <div className="rounded-lg p-3" style={{ background: 'rgba(255,255,255,0.04)' }}>
                      <p className="text-text-secondary text-xs mb-0.5">Est. Monthly KM</p>
                      <p className="text-text-primary text-sm font-medium">{reqDetails?.estimatedMileage?.toLocaleString()} km</p>
                    </div>
                  )}
                  {reqDetails?.address && (
                    <div className="rounded-lg p-3" style={{ background: 'rgba(255,255,255,0.04)' }}>
                      <p className="text-text-secondary text-xs mb-0.5">Address</p>
                      <p className="text-text-primary text-sm font-medium">{reqDetails?.address}</p>
                    </div>
                  )}
                </div>

                {/* Requested Options */}
                {reqOptions?.length > 0 && (
                  <div>
                    <p className="text-text-secondary text-xs mb-2">Requested Options</p>
                    <div className="flex flex-wrap gap-2">
                      {reqOptions?.map(opt => (
                        <span key={opt} className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium" style={{ background: 'rgba(52,211,153,0.1)', color: '#34d399', border: '1px solid rgba(52,211,153,0.2)' }}>
                          <Icon name="Check" size={10} /> {opt}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Notes from submitter */}
                {req?.notes && (
                  <div className="rounded-lg p-3" style={{ background: 'rgba(255,255,255,0.04)' }}>
                    <p className="text-text-secondary text-xs mb-1">Notes from Submitter</p>
                    <p className="text-text-primary text-sm leading-relaxed">{req?.notes}</p>
                  </div>
                )}

                {/* Admin Notes */}
                {req?.admin_notes && (
                  <div className="rounded-lg p-3" style={{ background: 'rgba(255,215,0,0.05)', border: '1px solid rgba(255,215,0,0.15)' }}>
                    <p className="text-[#ffd700] text-xs mb-1 font-semibold">Admin Notes</p>
                    <p className="text-text-primary text-sm">{req?.admin_notes}</p>
                  </div>
                )}

                {/* Admin Note Input (only for pending/reviewing) */}
                {['pending', 'reviewing']?.includes(req?.intake_status) && (
                  <div>
                    <label className="text-text-secondary text-xs font-semibold uppercase tracking-wider mb-1.5 block">
                      Add Admin Note (optional)
                    </label>
                    <textarea
                      className="w-full px-3 py-2 rounded-lg text-sm text-text-primary outline-none focus:ring-1 focus:ring-[#ffd700]/30"
                      style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', resize: 'vertical', minHeight: '60px' }}
                      placeholder="Internal notes about this request..."
                      value={adminNotes?.[req?.id] || ''}
                      onChange={e => setAdminNotes(n => ({ ...n, [req?.id]: e?.target?.value }))}
                    />
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2 pt-1">
                  {/* Contact */}
                  <a
                    href={`mailto:${req?.email}`}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all hover:opacity-80"
                    style={{ background: 'rgba(96,165,250,0.12)', color: '#60a5fa', border: '1px solid rgba(96,165,250,0.25)' }}
                  >
                    <Icon name="Mail" size={13} /> Contact
                  </a>

                  {/* Mark as Reviewing */}
                  {req?.intake_status === 'pending' && (
                    <button
                      onClick={() => updateStatus(req?.id, 'reviewing')}
                      disabled={actionLoading === req?.id + 'reviewing'}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all hover:opacity-80 disabled:opacity-50"
                      style={{ background: 'rgba(96,165,250,0.12)', color: '#60a5fa', border: '1px solid rgba(96,165,250,0.25)' }}
                    >
                      <Icon name="Eye" size={13} />
                      {actionLoading === req?.id + 'reviewing' ? 'Updating...' : 'Mark Reviewing'}
                    </button>
                  )}

                  {/* Approve */}
                  {['pending', 'reviewing']?.includes(req?.intake_status) && (
                    <button
                      onClick={() => updateStatus(req?.id, 'approved')}
                      disabled={actionLoading === req?.id + 'approved'}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all hover:opacity-80 disabled:opacity-50"
                      style={{ background: 'rgba(52,211,153,0.12)', color: '#34d399', border: '1px solid rgba(52,211,153,0.25)' }}
                    >
                      <Icon name="CheckCircle" size={13} />
                      {actionLoading === req?.id + 'approved' ? 'Approving...' : 'Approve'}
                    </button>
                  )}

                  {/* Reject */}
                  {['pending', 'reviewing']?.includes(req?.intake_status) && (
                    <button
                      onClick={() => updateStatus(req?.id, 'rejected')}
                      disabled={actionLoading === req?.id + 'rejected'}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all hover:opacity-80 disabled:opacity-50"
                      style={{ background: 'rgba(239,68,68,0.12)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.25)' }}
                    >
                      <Icon name="XCircle" size={13} />
                      {actionLoading === req?.id + 'rejected' ? 'Rejecting...' : 'Reject'}
                    </button>
                  )}

                  {/* Convert to Corporate Account */}
                  {req?.intake_status === 'approved' && !req?.corporate_id && (
                    <button
                      onClick={() => setConvertModal({ request: req })}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all hover:opacity-80"
                      style={{ background: 'rgba(255,215,0,0.12)', color: '#ffd700', border: '1px solid rgba(255,215,0,0.25)' }}
                    >
                      <Icon name="Building" size={13} /> Convert to Account
                    </button>
                  )}

                  {/* Already converted badge */}
                  {req?.corporate_id && (
                    <span className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold" style={{ background: 'rgba(52,211,153,0.08)', color: '#34d399', border: '1px solid rgba(52,211,153,0.2)' }}>
                      <Icon name="CheckCircle" size={13} /> Account Created
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Convert to Account Modal */}
      {convertModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="rounded-2xl p-6 max-w-md w-full" style={{ background: '#0d1f3c', border: '1px solid rgba(255,255,255,0.12)' }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,215,0,0.12)' }}>
                <Icon name="Building" size={20} className="text-[#ffd700]" />
              </div>
              <div>
                <h3 className="text-white font-bold text-base">Convert to Corporate Account</h3>
                <p className="text-white/50 text-xs">This will create a vetted corporate account</p>
              </div>
            </div>

            <div className="rounded-xl p-4 mb-5 space-y-2" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <div className="flex justify-between text-sm">
                <span className="text-white/50">Company</span>
                <span className="text-white font-medium">{convertModal?.request?.company_name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-white/50">Contact</span>
                <span className="text-white font-medium">{convertModal?.request?.contact_name || '—'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-white/50">Email</span>
                <span className="text-white font-medium">{convertModal?.request?.email}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-white/50">Fleet Size</span>
                <span className="text-white font-medium">{convertModal?.request?.fleet_size} vehicles</span>
              </div>
            </div>

            <p className="text-white/40 text-xs mb-5">
              A new corporate account will be created with status <strong className="text-white/70">Active</strong>. The original request will be linked via <code className="text-[#ffd700]">source_request_id</code> for full traceability.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setConvertModal(null)}
                disabled={convertLoading}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all hover:bg-white/10"
                style={{ border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.7)' }}
              >
                Cancel
              </button>
              <button
                onClick={handleConvertToAccount}
                disabled={convertLoading}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold transition-all hover:opacity-90 disabled:opacity-50"
                style={{ background: '#ffd700', color: '#0a1628' }}
              >
                {convertLoading ? 'Creating...' : 'Create Account'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div
          className="fixed bottom-6 right-6 z-50 px-4 py-3 rounded-xl text-sm font-semibold shadow-xl flex items-center gap-2"
          style={{
            background: toast?.type === 'error' ? 'rgba(239,68,68,0.95)' : 'rgba(52,211,153,0.95)',
            color: 'white',
          }}
        >
          <Icon name={toast?.type === 'error' ? 'XCircle' : 'CheckCircle'} size={16} />
          {toast?.msg}
        </div>
      )}
    </div>
  );
};

export default CorporateLeaseRequestsQueue;
